import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { change } from 'redux-form';
import LazyLoad from 'react-lazyload';
import { get, debounce, map, isEmpty, includes } from 'lodash';
import classnames from 'classnames';
import Card from '../elements-card/Card';
import QuantityStepper from '../elements-inputs/QuantityStepper';
import categoryIcons from '../app-bookings-update/data/addOnCategoryIcons';
import ModalManager from '../elements-modal/modal-manager-v2';
import { ItemDetailsModal } from '../app-bookings-custom/modals/item-details-modal';
import { priceDisplayString, priceOptionLabel } from '../app-bookings-custom/utils';
import { FormattedMessage, FormattedNumber, injectIntl, intlShape } from 'react-intl';

class CheckoutAddOnCard extends Component {
  constructor (props) {
    super(props);

    this.validateAddOn = this.validateAddOn.bind(this);
    this.onDecrementClick = this.onDecrementClick.bind(this);
    this.onIncrementClick = this.onIncrementClick.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);

    this.debouncedOnChange = debounce(props.handleAddOnChange, 300);
  }

  async onDecrementClick (e) {
    const { dispatch, field, selectedAddOn, formName } = this.props;
    const subtractedCount = selectedAddOn.info.count - 1;

    if (0 <= subtractedCount) {
      await dispatch(change(formName, `${field}.info.count`, subtractedCount));
      return this.debouncedOnChange(e, subtractedCount);
    }
  }

  async onIncrementClick (e) {
    const { dispatch, field, selectedAddOn, addOn, formName } = this.props;
    const maxVal = get(addOn, 'count', null);
    const currentCount = selectedAddOn.info.count || 0;
    const addedCount = currentCount + 1;

    if (maxVal && maxVal >= addedCount) {
      await dispatch(change(formName, `${field}.info.count`, addedCount));
      return this.debouncedOnChange(e, addedCount);
    }

    await dispatch(change(formName, `${field}.info.count`, addedCount));
    return this.debouncedOnChange(e, addedCount);
  }

  async handleInputChange (e, newVal) {
    const { field, dispatch, formName } = this.props;

    await dispatch(change(formName, `${field}.info.count`, newVal));
    return this.debouncedOnChange(e, newVal);
  }

  validateAddOn (val) {
    const maxVal = get(this.props.addOn, 'count');
    const isConcierge = get(this.props.addOn, 'is_concierge');

    if (isConcierge) {
      return (10 > val && 0 < val)
        ? this.props.intl.formatMessage({ id: 'inbox.checkout_add_on_card.minimum_quantity', defaultMessage: 'The minimum quantity is {value}' }, { value: this.props.intl.formatNumber(10) })
        : undefined;
    }
    return (0 < maxVal && maxVal < val)
      ? this.props.intl.formatMessage({ id: 'inbox.checkout_add_on_card.maximum_quantity', defaultMessage: 'The max quantity is {value}' }, { value: this.props.intl.formatNumber(maxVal) })
      : 9999 < val
        ? this.props.intl.formatMessage({ id: 'inbox.checkout_add_on_card.enter_quantity', defaultMessage: 'Enter a quantity less than {value}' }, { value: this.props.intl.formatNumber(9999) })
        : undefined;
  }

  render () {
    const { field, addOn = {}, selectedAddOn, index, toggleModal, whichModal } = this.props;
    const currency = get(addOn, 'currency.code', 'USD');
    // Allow decimal inputs for items that are charged per hour
    const isPerHour = 'per_hour' === addOn.unit_cost_basis;
    const parseValue = (val) => {
      if (val) {
        return isPerHour ? Math.abs(parseFloat(val)) : Math.abs(parseInt(val));
      }
      return null;
    };

    const val = get(selectedAddOn, 'info.count', null);
    const maxVal = get(addOn, 'count');
    const conciergeMinVal = get(addOn, 'min_count');
    const isValidQuantity = get(addOn, 'is_concierge') ?
      val >= get(addOn, 'min_count')
      :
      maxVal ?
      val !== null && 0 < val && val <= maxVal
      :
      val !== null && 0 < val;

    const currentCount = get(selectedAddOn, 'info.count', null);
    const addtlCharges = get(addOn, 'additional_charges', []);
    const isConciergeItem = get(addOn, 'is_concierge');
    const backgroundImg = get(addOn, 'image_ids[0]');
    const imgUrl = `https://img.peerspace.com/image/upload/${backgroundImg}.jpg`;

    const itemCategory = get(addOn, 'category');
    const itemSubcategory = get(addOn, 'subcategory');
    const uniqueSubcategory = includes(['non-alcoholic_drinks', 'alcoholic_drinks', 'full_bar_package',
      'bar_services', 'backdrops', 'lighting', 'props', 'grips', 'power_supplies', 'ladder'], itemSubcategory);
    const ItemIcon = uniqueSubcategory ? categoryIcons[itemSubcategory] : categoryIcons[itemCategory];
    const addOnPlaceholderDisplay = (
      <div className="flex column center">
        <div>
          {ItemIcon
            && <ItemIcon
              className="icon addon-icon"
              alt={`${uniqueSubcategory ? itemSubcategory : itemCategory} Icon`}
            />
          }
        </div>
      </div>
    );

    const addOnId = addOn._id || addOn.item_id;
    const toggleItemDescriptionModal = () => {
      toggleModal(`itemDescriptionModal-${addOnId}`);
    };

    return (
      <Card className={classnames('AddOnCard', { 'background-img': !!backgroundImg })} cardClassName={classnames({ selectedCard: (isValidQuantity) })} column>
        <ModalManager whichModal={whichModal} toggleModal={toggleModal}>
          <ItemDetailsModal
            id={`itemDescriptionModal-${addOnId}`}
            toggleModal={toggleModal}
            item={addOn}
            whichModal={whichModal}
          />
        </ModalManager>
        <div className="flex add-on-info-container">

          <LazyLoad offset={25} once>
            <div className={classnames('add-on-image margin-lg-right flex center middle', { 'placeholder hidden-xs': !backgroundImg })} style={{ backgroundImage: backgroundImg ? `url(${imgUrl})` : null }}>
              <div className={classnames('circle-check', { hidden: !isValidQuantity }, { 'visible-xs': isValidQuantity })}>
                <img className="v-baseline" src="/web-2/assets/images/circle-check.svg?v=rb" alt="circle-check" />
              </div>
              { !backgroundImg && addOnPlaceholderDisplay }
            </div>
          </LazyLoad>

          <div className="add-on-details flex column full-width">
            <div className="flex space-between">
              <h6
                className="no-top-margin margin-sm-bottom text-base"
                data-testing-id='add-on-label'
              >
                {addOn.label}
              </h6>
              <div className={classnames('circle-check', { 'hidden-xs': !!backgroundImg }, { hidden: !isValidQuantity })}>
                <img className="v-baseline" src="/web-2/assets/images/circle-check.svg?v=rb" alt="circle-check" />
              </div>
            </div>
            <div className="flex margin-sm-bottom text-small">
              <div data-testing-id='add-on-price'>
                <FormattedMessage
                  id="inbox.item_details.price"
                  defaultMessage="{price}{space}{optionalLabel}"
                  values={{
                    price: <FormattedNumber value={addOn.unit_cost} currency={currency} style='currency' />,
                    optionalLabel: <FormattedMessage { ...priceOptionLabel[addOn.unit_cost_basis] } />,
                    space: 'flat_fee' === addOn.unit_cost_basis ? ' ' : ''
                  }}
                />
              </div>
              {
                addOn.count > 0 &&
                <div>
                  <span className="margin-sm-left margin-sm-right">&bull;</span>
                  {`Max ${addOn.count}`}
                </div>
              }
            </div>

            <button
              onClick={toggleItemDescriptionModal}
              type="button"
              data-testing-id='info-modal-btn'
            >
              <FormattedMessage
                id="booking_request.checkout_card.more_info"
                defaultMessage="More info"
              />
            </button>

          </div>
        </div>

        <div>
          <QuantityStepper
            name={`${field}.info.count`}
            placeholder="0"
            parse={parseValue}
            onChange={this.handleInputChange}
            validate={this.validateAddOn}
            onDecrementClick={this.onDecrementClick}
            onIncrementClick={this.onIncrementClick}
            disableDecrementBtn={!currentCount || 0 === currentCount}
            disableIncrementBtn={maxVal > 0 && (maxVal < currentCount || maxVal === currentCount)}
            showErrorOnChange
            index={index}
          />
        </div>
      </Card>
    )
  }
}

CheckoutAddOnCard.propTypes = {
  field: PropTypes.string,
  addOn: PropTypes.object,
  handleAddOnChange: PropTypes.func,
  intl: intlShape
};

export default injectIntl(CheckoutAddOnCard);
